import type { UserRole } from '../contexts/PermissionsContext';

/**
 * Normalised roles (see PermissionsContext) whose users own or manage their
 * own catalog. These users always see the Catalog tab and default to their
 * own catalog.
 *
 * NOTE: these are the *normalised* hyphenated role strings produced by
 * PermissionsContext — raw Cognito ``custom:userRole`` values like
 * ``catalog_owner`` / ``commercial`` are normalised upstream and never reach
 * callers of this helper. ActiveCatalogContext keeps a separate set that also
 * accepts the raw values because it reads ``useAuth().user.role`` directly.
 */
export const OWNER_ROLES: ReadonlySet<UserRole> = new Set([
  'catalog-owner',
  'admin',
  'legacy-manager',
]);

/** True when ``role`` owns/manages a catalog of their own. */
export const isOwnerRole = (role: string | null | undefined): boolean =>
  OWNER_ROLES.has(role as UserRole);

/**
 * Roles that land on the unified Catalog view (``/my-objects``) after login.
 *
 * Owners/managers land on their own catalog; delegates land there too because
 * — since the standalone delegate dashboard was removed — they use the same
 * Catalog view and dropdown switcher as everyone else. Viewers are the only
 * authenticated role that does NOT land here (they go to ``/home``).
 */
export const landsOnCatalogView = (role: string | null | undefined): boolean =>
  isOwnerRole(role) || role === 'delegate';
